import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getChronicleEntries, type ChronicleEntry } from '@/services/chronicle';
import { useTelegramBackButton } from '@/hooks/useTelegramBackButton';

interface ChroniclePageProps {
  onClose: () => void;
}

const TRIGGER_ICONS: Record<string, string> = {
  meditation: '🧘',
  breathing: '💨',
  reflection: '📝',
  journal: '📖',
  battery_b: '🔮',
  battery_c: '⚡',
  stage_complete: '🏔️',
  event: '🎭',
};

const MOOD_COLORS: Record<string, string> = {
  awakening: 'from-amber-500/20 to-orange-500/20',
  discovery: 'from-purple-500/20 to-indigo-500/20',
  rest: 'from-green-500/20 to-teal-500/20',
  wonder: 'from-blue-500/20 to-cyan-500/20',
  melancholy: 'from-slate-500/20 to-gray-500/20',
};

export function ChroniclePage({ onClose }: ChroniclePageProps) {
  const [entries, setEntries] = useState<ChronicleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Telegram BackButton
  useTelegramBackButton(onClose);

  const loadEntries = useCallback(async (pageNum: number, append: boolean = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const result = await getChronicleEntries(pageNum, 10);

      if (append) {
        setEntries(prev => [...prev, ...result.entries]);
      } else {
        setEntries(result.entries);
      }

      setHasMore(pageNum < result.pagination.totalPages);
    } catch (error) {
      console.error('Failed to load chronicle entries:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadEntries(1);
  }, [loadEntries]);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadEntries(nextPage, true);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-950 via-primary-900 to-primary-950 safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-primary-950/90 backdrop-blur-sm border-b border-primary-800/50 p-4">
        <div className="flex items-center justify-center">
          <h1 className="text-lg font-bold text-white">Хроника</h1>
        </div>
      </div>

      {/* City illustration header */}
      <div className="relative h-40 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-800/30 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <span className="text-6xl">🏙️</span>
            <p className="text-primary-400 mt-2 italic text-sm">
              Записи из параллельного города
            </p>
          </div>
        </div>
        {/* Stars */}
        <div className="absolute top-4 left-8 text-xs text-primary-600">✦</div>
        <div className="absolute top-8 right-12 text-xs text-primary-500">✧</div>
        <div className="absolute top-12 left-20 text-xs text-primary-600">✦</div>
        <div className="absolute top-6 right-24 text-xs text-primary-500">✧</div>
      </div>

      {/* Entries list */}
      <div className="px-4 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-primary-400">Загрузка летописи...</div>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <span className="text-5xl mb-4 block">📜</span>
            <p className="text-primary-400">
              Летопись пуста. Выполни первую практику,
              <br />
              чтобы начать историю.
            </p>
          </div>
        ) : (
          <AnimatePresence>
            <div className="space-y-4">
              {entries.map((entry, index) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`relative rounded-xl p-5 bg-gradient-to-br ${
                    MOOD_COLORS[entry.mood || 'discovery'] || MOOD_COLORS.discovery
                  } border border-primary-700/30`}
                >
                  {/* Decorative corner */}
                  <div className="absolute top-2 right-3 text-primary-600/40 text-xs">
                    ❦
                  </div>

                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">
                      {TRIGGER_ICONS[entry.triggerType] || '✨'}
                    </span>
                    <span className="text-xs text-primary-500">
                      {formatDate(entry.createdAt)}
                    </span>
                  </div>

                  {/* Narrative */}
                  <p className="text-primary-200 italic leading-relaxed text-sm">
                    "{entry.narrative}"
                  </p>

                  {/* Fatigue change */}
                  {entry.fatigueBefore !== null && entry.fatigueAfter !== null &&
                    !(entry.fatigueBefore === 0 && entry.fatigueAfter === 0) && (
                    <div className="mt-3 text-xs text-primary-500">
                      Усталость: {entry.fatigueBefore} → {entry.fatigueAfter}
                    </div>
                  )}
                </motion.div>
              ))}

              {/* Load more button */}
              {hasMore && (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full py-3 text-primary-400 text-sm hover:text-primary-300 transition-colors"
                >
                  {loadingMore ? 'Загрузка...' : 'Показать ещё'}
                </button>
              )}

              {/* Back to city */}
              <button
                onClick={onClose}
                className="w-4/5 mx-auto block py-4 mt-6 mb-2 rounded-2xl bg-accent-warm text-white text-lg font-medium"
              >
                ← В город
              </button>
            </div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
